import { getErrorMessage } from "../../utils/apiError";
import { parseDateKey } from "../../utils/date";
import { toManilaParts } from "../../utils/manilaTime";
import { getEmployeeId, getStoredUser } from "../../utils/session";
import { cx } from "../../utils/classNames";
import { useDashboard } from "./useDashboard";
import DashboardCard from "./cards/DashboardCard";
import CheckInPanel from "../attendance/CheckInPanel";
import ActionItemsCard from "./cards/ActionItemsCard";
import ThisPeriodCard from "./cards/ThisPeriodCard";
import OvertimeCard from "./cards/OvertimeCard";
import LeaveCard from "./cards/LeaveCard";
import AssetsCard from "./cards/AssetsCard";
import { useDashboardWidgets } from "../../../api/queries/widgetQueries";
import {
  AnnouncementList,
  BirthdayList,
  HolidayList,
} from "../../../components/dashboard/WidgetLists";
import ui from "../../components/ui/ui.module.css";
import styles from "./Dashboard.module.css";

const getGreeting = () => {
  const hour = Number(toManilaParts(new Date().toISOString()).time.slice(0, 2));
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const formatLongDate = (dateKey) =>
  parseDateKey(dateKey)?.toLocaleDateString("en-PH", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const LOADING = "loading";
const WidgetSpinner = () => (
  <div className="text-center py-3">
    <span
      className="spinner-border spinner-border-sm"
      role="status"
      aria-label="Loading"
    ></span>
  </div>
);

const DashboardView = () => {
  const query = useDashboard();
  // Announcements, holidays and birthdays (shared with the HR view)
  const widgets = useDashboardWidgets({ enabled: !!getEmployeeId() });
  const widget = (key) =>
    widgets.isLoading ? LOADING : (widgets.data?.[key] ?? null);
  const widgetRetry = {
    onRetry: () => widgets.refetch(),
    isRetrying: widgets.isFetching,
  };
  const data = query.data;
  const retry = {
    onRetry: () => query.refetch(),
    isRetrying: query.isFetching,
  };

  if (!getEmployeeId() || query.error?.response?.status === 403) {
    const user = getStoredUser();
    return (
      <div className={ui.page}>
        <h2 className={styles.greeting}>
          {getGreeting()}
          {user.name ? `, ${user.name}` : ""}
        </h2>
        <div className={cx(ui.alert, ui.alertGray)}>
          Your account is not linked to an employee record.
        </div>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className={cx(ui.card, ui.empty)}>
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        Loading your dashboard…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        <span>
          {getErrorMessage(query.error, "Unable to load your dashboard.")}
        </span>
        <button
          type="button"
          className={cx(ui.button, ui.secondary, ui.buttonSmall)}
          onClick={() => query.refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  const { employee } = data;
  const subtitle = [
    employee?.roleTitle,
    employee?.branchName,
    formatLongDate(data.today),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={ui.page}>
      <div>
        <h2 className={styles.greeting}>
          {getGreeting()}
          {employee?.firstName ? `, ${employee.firstName}` : ""} 👋
        </h2>
        <p className={styles.greetingMeta}>{subtitle}</p>
      </div>

      <div className={styles.grid}>
        <div className={styles.span4}>
          {/* Loads its own data (GET /attendance/me/today + server clock). */}
          <DashboardCard title="Today" data={true}>
            {() => <CheckInPanel />}
          </DashboardCard>
        </div>

        <div className={styles.span8}>
          <DashboardCard
            title="Needs your attention"
            data={data.actionItems ?? []}
            {...retry}
          >
            {(items) => <ActionItemsCard items={items} />}
          </DashboardCard>
        </div>

        <div className={styles.span8}>
          <DashboardCard
            title="Announcements"
            data={widget("announcements")}
            {...widgetRetry}
          >
            {(items) =>
              items === LOADING ? (
                <WidgetSpinner />
              ) : (
                <AnnouncementList items={items} />
              )
            }
          </DashboardCard>
        </div>

        <div className={styles.span4}>
          <DashboardCard
            title="Birthdays"
            data={widget("birthdays")}
            {...widgetRetry}
          >
            {(items) =>
              items === LOADING ? (
                <WidgetSpinner />
              ) : (
                <BirthdayList items={items} />
              )
            }
          </DashboardCard>
        </div>

        <div className={styles.span4}>
          <DashboardCard
            title="Upcoming holidays"
            data={widget("holidays")}
            {...widgetRetry}
          >
            {(items) =>
              items === LOADING ? (
                <WidgetSpinner />
              ) : (
                <HolidayList items={items} />
              )
            }
          </DashboardCard>
        </div>

        <div className={styles.span8}>
          <DashboardCard
            title={`This cut-off${data.thisPeriod?.period?.label ? ` (${data.thisPeriod.period.label})` : ""}`}
            data={data.thisPeriod}
            {...retry}
          >
            {(cutoff) => <ThisPeriodCard cutoff={cutoff} />}
          </DashboardCard>
        </div>

        <div className={styles.span6}>
          <DashboardCard
            title={`Leave ${data.leave?.year ?? ""}`}
            data={data.leave}
            {...retry}
          >
            {(leave) => <LeaveCard leave={leave} />}
          </DashboardCard>
        </div>

        <div className={styles.span6}>
          <DashboardCard title="My assets" data={data.assets} {...retry}>
            {(assets) => <AssetsCard assets={assets} />}
          </DashboardCard>
        </div>

        <div className={styles.span6}>
          <DashboardCard title="Overtime" data={data.overtime} {...retry}>
            {(overtime) => <OvertimeCard overtime={overtime} />}
          </DashboardCard>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
